import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { brandColors, getGradeColor } from '@config/brandTokens';
import {
  contestScoreToGrade,
  getCountryFlagUrl,
  latLngToMapPercent,
  resolveCityCoords,
  resolveCountryMeta,
} from '@utils/contestGeos';

export interface LeaderboardMapEntry {
  rank: number;
  playerName: string;
  organizationName?: string | null;
  city?: string | null;
  zone?: string | null;
  state?: string | null;
  country?: string | null;
  countryCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  totalScore: number;
  avgScore?: number;
  skillsCounted?: number;
}

interface PlacePerson {
  key: string;
  playerName: string;
  rank: number;
  grade: string;
  score: number;
  city: string;
  country?: string | null;
  countryCode?: string | null;
  organizationName?: string | null;
}

interface MapPlace {
  id: string;
  city: string;
  country?: string | null;
  countryCode?: string | null;
  left: number;
  top: number;
  people: PlacePerson[];
  /** Dominant grade among people at this place (best score). */
  grade: string;
}

const WORLD_MAP_URL =
  'https://upload.wikimedia.org/wikipedia/commons/thumb/8/80/World_map_-_low_resolution.svg/1280px-World_map_-_low_resolution.svg.png';

const GRADE_LEGEND = ['D', 'C', 'B', 'A', 'A+'] as const;

function entryScore(entry: LeaderboardMapEntry): number {
  if (entry.avgScore != null && Number.isFinite(Number(entry.avgScore))) {
    return Number(entry.avgScore);
  }
  return Number(entry.totalScore) || 0;
}

function buildPlaces(entries: LeaderboardMapEntry[]): MapPlace[] {
  const byCity = new Map<string, MapPlace>();

  for (const entry of entries) {
    const coords = resolveCityCoords(entry.city, entry.latitude, entry.longitude);
    if (!coords || !entry.city) continue;

    const cityKey = String(entry.city).trim().toLowerCase();
    const score = entryScore(entry);
    const grade = contestScoreToGrade(score);
    const person: PlacePerson = {
      key: `${entry.rank}-${entry.playerName}-${cityKey}`,
      playerName: entry.playerName,
      rank: entry.rank,
      grade,
      score,
      city: entry.city,
      country: entry.country,
      countryCode: entry.countryCode,
      organizationName: entry.organizationName,
    };

    const existing = byCity.get(cityKey);
    if (existing) {
      existing.people.push(person);
      const best = existing.people.reduce((a, b) => (a.score >= b.score ? a : b));
      existing.grade = best.grade;
      continue;
    }

    const { left, top } = latLngToMapPercent(coords.lat, coords.lng);
    byCity.set(cityKey, {
      id: cityKey,
      city: entry.city,
      country: entry.country,
      countryCode: entry.countryCode,
      left,
      top,
      people: [person],
      grade,
    });
  }

  return Array.from(byCity.values());
}

interface ContestLeaderboardMapPanelProps {
  entries: LeaderboardMapEntry[];
  selectedPlayerKey: string | null;
  onSelectPlayer: (key: string | null) => void;
}

const ContestLeaderboardMapPanel: React.FC<ContestLeaderboardMapPanelProps> = ({
  entries,
  selectedPlayerKey,
  onSelectPlayer,
}) => {
  const places = useMemo(() => buildPlaces(entries), [entries]);
  const [hoveredPlaceId, setHoveredPlaceId] = useState<string | null>(null);

  const selectedPlaceId = useMemo(() => {
    if (!selectedPlayerKey) return null;
    for (const place of places) {
      if (place.people.some((p) => p.key === selectedPlayerKey)) return place.id;
    }
    return null;
  }, [places, selectedPlayerKey]);

  useEffect(() => {
    if (selectedPlaceId) setHoveredPlaceId(selectedPlaceId);
  }, [selectedPlaceId]);

  const flatPeople = useMemo(
    () =>
      places
        .flatMap((p) => p.people)
        .sort((a, b) => a.rank - b.rank),
    [places],
  );

  const focusedPlace = places.find((p) => p.id === (hoveredPlaceId || selectedPlaceId));

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '220px 1fr' },
        gap: 1.5,
        minHeight: { xs: 420, lg: 360 },
        flex: 1,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          bgcolor: brandColors.white,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: { xs: 220, md: '100%' },
        }}
      >
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: brandColors.navy }}>
            Players on map
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Select a person to highlight their city
          </Typography>
        </Box>
        <List dense disablePadding sx={{ overflow: 'auto', flex: 1 }}>
          {flatPeople.map((person) => {
            const colors = getGradeColor(person.grade);
            const selected = selectedPlayerKey === person.key;
            return (
              <ListItemButton
                key={person.key}
                selected={selected}
                onClick={() => onSelectPlayer(selected ? null : person.key)}
                sx={{
                  alignItems: 'flex-start',
                  borderLeft: selected ? `3px solid ${colors.text}` : '3px solid transparent',
                }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: brandColors.navy }}>
                        #{person.rank} {person.playerName}
                      </Typography>
                      <Chip
                        size="small"
                        label={person.grade}
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          fontWeight: 800,
                          bgcolor: colors.bg,
                          color: colors.text,
                          border: `1px solid ${colors.border}`,
                        }}
                      />
                    </Stack>
                  }
                  secondary={`${person.city}${person.country ? ` · ${person.country}` : ''}`}
                  secondaryTypographyProps={{ sx: { fontSize: '0.7rem' } }}
                />
              </ListItemButton>
            );
          })}
          {flatPeople.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No mapped cities for the current filters.
              </Typography>
            </Box>
          )}
        </List>
      </Box>

      <Box
        sx={{
          position: 'relative',
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#dbeafe',
          minHeight: 300,
        }}
      >
        <Box
          component="img"
          src={WORLD_MAP_URL}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.88,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />

        {places.map((place) => {
          const colors = getGradeColor(place.grade);
          const isFocus = place.id === hoveredPlaceId || place.id === selectedPlaceId;
          const isSelected =
            selectedPlaceId === place.id ||
            place.people.some((p) => p.key === selectedPlayerKey);

          return (
            <Box
              key={place.id}
              onMouseEnter={() => setHoveredPlaceId(place.id)}
              onMouseLeave={() => setHoveredPlaceId(selectedPlaceId)}
              onClick={() => {
                const top = place.people[0];
                if (top) onSelectPlayer(top.key);
              }}
              sx={{
                position: 'absolute',
                left: `${place.left}%`,
                top: `${place.top}%`,
                width: isFocus ? 16 : 11,
                height: isFocus ? 16 : 11,
                ml: isFocus ? '-8px' : '-5.5px',
                mt: isFocus ? '-8px' : '-5.5px',
                borderRadius: '50%',
                bgcolor: colors.text,
                border: `2px solid ${brandColors.white}`,
                boxShadow: isFocus
                  ? `0 0 0 4px ${colors.border}, 0 2px 8px rgba(15,23,42,0.35)`
                  : '0 1px 4px rgba(15,23,42,0.35)',
                cursor: 'pointer',
                zIndex: isFocus ? 4 : 2,
                transition: 'width 160ms ease, height 160ms ease, box-shadow 160ms ease',
                '@keyframes pulseDot': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.18)' },
                },
                animation: isSelected ? 'pulseDot 1.2s ease-in-out infinite' : 'none',
              }}
            />
          );
        })}

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{
            position: 'absolute',
            left: 10,
            bottom: 10,
            right: 10,
            zIndex: 5,
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.68rem',
              fontWeight: 700,
              color: brandColors.navy,
              bgcolor: 'rgba(255,255,255,0.92)',
              px: 0.75,
              py: 0.35,
              borderRadius: 1,
            }}
          >
            Grade
          </Typography>
          {GRADE_LEGEND.map((code) => {
            const c = getGradeColor(code);
            return (
              <Stack
                key={code}
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.92)',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 1,
                  border: `1px solid ${c.border}`,
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.text }} />
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: c.text }}>
                  {code}
                </Typography>
              </Stack>
            );
          })}
        </Stack>

        {focusedPlace && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 5,
              maxWidth: 260,
              bgcolor: 'rgba(255,255,255,0.96)',
              border: '1px solid #e2e8f0',
              borderRadius: 1.5,
              px: 1.25,
              py: 1,
              boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
              {(() => {
                const meta = resolveCountryMeta(focusedPlace.country, focusedPlace.countryCode);
                const flagUrl = meta ? getCountryFlagUrl(meta.code, 20) : '';
                return flagUrl ? (
                  <Box
                    component="img"
                    src={flagUrl}
                    alt=""
                    sx={{ width: 16, height: 12, objectFit: 'cover', borderRadius: 0.25 }}
                  />
                ) : null;
              })()}
              <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: brandColors.navy }}>
                {focusedPlace.city}
              </Typography>
            </Stack>
            {focusedPlace.people.map((p) => {
              const colors = getGradeColor(p.grade);
              return (
                <Typography
                  key={p.key}
                  sx={{
                    fontSize: '0.75rem',
                    color: selectedPlayerKey === p.key ? colors.text : brandColors.slate,
                    fontWeight: selectedPlayerKey === p.key ? 800 : 500,
                  }}
                >
                  #{p.rank} {p.playerName} · {p.grade}
                </Typography>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ContestLeaderboardMapPanel;
